"use client";

// =========================================================================
// TimelineEmailBody — inline, scrollable full email body in the timeline.
//
// WHY: for HTML emails the stored `body_preview` is the first ~280 chars of raw
// markup (usually just `<head><style>…`), which sanitizes to nothing — so the
// timeline bubble showed no body. The real body lives in S3 (COMMS_BODY_S3_AND_FTS).
//
// UX: the body loads automatically — no click-to-expand. Rows near the top of
// the timeline (`eager`) fetch on mount; the rest fetch as they scroll into the
// viewport (IntersectionObserver with a look-ahead margin), so we only ever pull
// from S3 what the user is actually about to read. A subtle collapse toggle lets
// a long email be hidden after it loads, but reading it never requires a click.
// =========================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useAuth } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";

interface TimelineEmailBodyProps {
  communicationId: string;
  /** Preview markup already on the row — shown until the full body loads. */
  previewBody?: string;
  /** Fetch the full body immediately on mount (used for the top rows). */
  eager?: boolean;
}

export function TimelineEmailBody({
  communicationId,
  previewBody,
  eager = false,
}: TimelineEmailBodyProps) {
  const { getAuthToken } = useAuth();
  const [fullBody, setFullBody] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const requestedRef = useRef(false);

  const loadBody = useCallback(async () => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/communications/${communicationId}/body`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Could not load the email (${res.status})`);
      const data = (await res.json()) as { body?: string };
      setFullBody(data.body ?? "");
    } catch (e) {
      requestedRef.current = false; // allow retry
      setError(e instanceof Error ? e.message : "Could not load the email");
    } finally {
      setLoading(false);
    }
  }, [communicationId, getAuthToken]);

  // Auto-load: eager rows on mount; the rest when they scroll near the viewport.
  useEffect(() => {
    if (eager) {
      void loadBody();
      return;
    }
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // No observer support (SSR / jsdom) — fall back to loading immediately.
      void loadBody();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadBody();
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }, // begin fetching just before the row is visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eager, loadBody]);

  const toggleCollapsed = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsed((prev) => !prev);
  }, []);

  return (
    <div ref={containerRef} onClick={(e) => e.stopPropagation()}>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1 py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading email…
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          <span className="text-xs text-destructive">{error}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              void loadBody();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && fullBody !== null && (
        <>
          {!collapsed && (
            <div className="max-h-96 overflow-y-auto rounded-md">
              <SafeEmailBody
                body={fullBody || previewBody || ""}
                variant="full"
                collapseQuotedReplies
                stopLinkClickPropagation
              />
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground -ml-2 mt-1"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronDown className="h-3.5 w-3.5 mr-1" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 mr-1" />
            )}
            {collapsed ? "Show email" : "Hide email"}
          </Button>
        </>
      )}
    </div>
  );
}
